// ETJump's snaphud (etj_snaphud_data.cpp): the yaw zones of 125 fps velocity
// snapping. Speed gained per frame is accel * frametime * wishspeed, and
// trap_SnapVector rounds each velocity component, so the gain is quantised
// into zones of wish direction: inside one zone the snapped gain is the same.
// The zone edges are where the x or y part of the per-frame gain crosses .5.

/** wishspeed may be scaled up to 3x by target_scale_velocity: 3 * 352 / 125 */
export const MAX_SNAP_ACCEL = 85;

/** AngleNormalizePI: -pi .. pi */
export function angleNormalizePI(angle: number): number {
  const twoPi = 2 * Math.PI;
  angle = (angle + Math.PI) % twoPi;
  return angle < 0 ? angle + Math.PI : angle - Math.PI;
}

/** SnaphudData::accelerate (trueness off: air acceleration also on the ground). */
export function snapAccel(wishspeed: number, msec: number): number {
  return Math.min(1 * wishspeed * (msec / 1000), MAX_SNAP_ACCEL);
}

/** SnaphudData::updateState: the zone edge angles of one quadrant (sorted, plus the first + pi/2). */
export function snapAngles(a: number): number[] {
  const out: number[] = [];
  if (!(a > 0)) return out;
  let step = Math.round(a) - 0.5;
  while (step > 0) {
    out.push(Math.acos(step / a));
    out.push(Math.asin(step / a));
    step -= 1;
  }
  out.sort((x, y) => x - y);
  // we might not have any valid angles on low speeds
  out.push(out.length ? out[0] + Math.PI / 2 : Math.PI / 2);
  return out;
}

export interface SnapZone {
  start: number;
  end: number;
  /** Every other zone (the two snaphud colours). */
  alt: boolean;
}

/** SnaphudV2::buildSnapZones: the zones of all four quadrants (world yaw, radians). */
export function snapZones(angles: number[]): SnapZone[] {
  const zones: SnapZone[] = [];
  for (let i = 0; i + 1 < angles.length; i++) {
    for (let q = 0; q < 4; q++) {
      const offset = (q * Math.PI) / 2;
      zones.push({ start: angles[i] + offset, end: angles[i + 1] + offset, alt: i % 2 === 1 });
    }
  }
  return zones;
}

/** Is `angle` inside the zone start..end (going counter-clockwise)? */
export function inZone(start: number, end: number, angle: number): boolean {
  const d = angleNormalizePI(angle - start);
  return d >= 0 && d <= angleNormalizePI(end - start);
}

export interface Wish {
  /** cmd scale * |wish velocity| */
  wishspeed: number;
  /** World yaw of the wish direction, radians. */
  angle: number;
}

/**
 * The wish direction and speed of movement keys (PM_CmdScale without the
 * upmove, as the snaphud computes them). No keys: ETJump's default input,
 * forward + right with sprint.
 */
export function wishOf(yawDeg: number, move: { forwardmove: number; rightmove: number }, sprint: boolean, speed = 320): Wish {
  let { forwardmove: fm, rightmove: rm } = move;
  if (!fm && !rm) {
    fm = rm = 127;
    sprint = true;
  }
  const max = Math.max(Math.abs(fm), Math.abs(rm));
  const total = Math.hypot(fm, rm);
  const scale = ((speed * max) / (127 * total)) * (sprint ? 1.1 : 0.8);
  const y = (yawDeg * Math.PI) / 180;
  // forward (cos, sin), right (sin, -cos)
  const wx = Math.cos(y) * fm + Math.sin(y) * rm;
  const wy = Math.sin(y) * fm - Math.cos(y) * rm;
  return { wishspeed: scale * Math.hypot(wx, wy), angle: Math.atan2(wy, wx) };
}

/**
 * PmoveUtilsV2::strafingForwards / rightStrafe: which side the optimal angle
 * is on (the player strafes right of the velocity).
 */
export function rightStrafe(speed: number, wishspeed: number, velAngle: number, wishAngle: number, move: { forwardmove: number; rightmove: number }): boolean {
  let forwards = false;
  if (!(speed < wishspeed || (!move.forwardmove && !move.rightmove))) {
    const diff = angleNormalizePI(wishAngle - velAngle);
    forwards = (move.rightmove < 0 && diff >= 0) || (move.rightmove > 0 && diff < 0) || (move.forwardmove !== 0 && diff >= 0);
  }
  return (forwards && move.rightmove > 0) || (!forwards && (move.rightmove < 0 || (move.forwardmove !== 0 && move.rightmove === 0)));
}

/**
 * SnaphudData::inMainAccelZone: the wish direction is in the same snap zone
 * as the optimal angle and on the gaining side of it.
 */
export function inMainAccelZone(angles: number[], speed: number, wishspeed: number, velAngle: number, wishAngle: number, optAngle: number, move: { forwardmove: number; rightmove: number }): boolean {
  if (angles.length < 2) return false;
  const right = rightStrafe(speed, wishspeed, velAngle, wishAngle, move);
  const opt = angleNormalizePI(right ? velAngle - optAngle : velAngle + optAngle);
  for (let i = 0; i + 1 < angles.length; i++) {
    for (let q = 0; q < 4; q++) {
      const offset = (q * Math.PI) / 2;
      const start = angles[i] + offset;
      const end = angles[i + 1] + offset;
      if (inZone(start, end, opt) && inZone(start, end, wishAngle)) {
        const distYaw = angleNormalizePI(wishAngle - start);
        const distOpt = angleNormalizePI(opt - start);
        // being equal to the optimal angle still gains the most
        return right ? distYaw <= distOpt : distYaw >= distOpt;
      }
    }
  }
  return false;
}
